import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FlaskConical, LogOut } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { markCrmEntered, resetCrmEntered } from "@/lib/platformEntry";
import { trpc } from "@/lib/trpc";
import { ActivityTab } from "./ActivityTab";
import { Errors } from "./Errors";
import { Orgs } from "./Orgs";
import { Overview } from "./Overview";
import { Btn, ToastProvider } from "./ui";

const TABS = [
  { value: "overview", label: "Zentrale" },
  { value: "orgs", label: "Betriebe" },
  { value: "errors", label: "Störungen" },
  { value: "activity", label: "Aktivität" },
] as const;

/** Plattform-Verwaltung im Maestro-Visuals-Stil — nur für den Betreiber (PLATFORM_ADMIN_EMAILS). */
export function Platform() {
  const access = trpc.platform.access.useQuery();
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [tab, setTab] = useState<(typeof TABS)[number]["value"]>("overview");
  const [orgId, setOrgId] = useState<string | null>(null);
  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      resetCrmEntered();
      utils.auth.me.invalidate();
    },
  });

  if (access.isLoading)
    return (
      <div className="pf pf-root" style={{ placeItems: "center", display: "grid" }}>
        <span className="pf-muted">Lädt …</span>
      </div>
    );
  if (!access.data?.allowed) return <Navigate to="/" replace />;

  return (
    <ToastProvider>
      <div className="pf pf-root">
        <header className="pf-head">
          <div className="pf-brand">
            <div className="pf-mark"><span>MV</span></div>
            <div className="pf-hide-sm">
              <div className="pf-brand-name">Maestro Visuals</div>
              <div className="pf-brand-sub">Plattform Control</div>
            </div>
          </div>
          <nav className="pf-nav" role="tablist" aria-label="Bereiche">
            {TABS.map((t) => (
              <button key={t.value} role="tab" aria-selected={tab === t.value} onClick={() => setTab(t.value)}>
                {t.label}
              </button>
            ))}
          </nav>
          <div className="pf-spacer" />
          <Btn
            variant="primary"
            onClick={() => {
              markCrmEntered();
              navigate("/");
            }}
          >
            <FlaskConical size={16} /> Testumgebung
          </Btn>
          <span className="pf-muted pf-hide-sm" style={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {me.data?.user.name || me.data?.user.email}
          </span>
          <Btn variant="ghost" size="sm" onClick={() => logout.mutate()} aria-label="Abmelden" title="Abmelden">
            <LogOut size={16} />
          </Btn>
        </header>
        <div className="pf-scroll">
          <div className="pf-wrap">
            <ErrorBoundary resetKey={tab}>
              {tab === "overview" && (
                <Overview
                  onOpenOrg={(id) => {
                    setOrgId(id);
                    setTab("orgs");
                  }}
                />
              )}
              {tab === "orgs" && <Orgs selectedId={orgId} onSelect={setOrgId} />}
              {tab === "errors" && <Errors />}
              {tab === "activity" && <ActivityTab />}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </ToastProvider>
  );
}
